import { ca } from '../i18n/ca';
import { splitSeconds } from './useNow';

/**
 * When a session that has not started starts, drawn the way a person reads a
 * calendar: a date tile (month, day, weekday), the hour, and a countdown in
 * days, hours and minutes that ticks. The session page's banner and the cards
 * of the sessions list both draw it, so a night waiting to begin reads the same
 * on both. A session with no hour (a director starts it by hand, or it starts
 * when enough players are on) says so in the same place rather than drawing
 * nothing, which is how a countdown went missing for the owner (2026-10-13).
 */

const SECOND = 1000;

const two = (n: number) => String(n).padStart(2, '0');

/** The calendar tile: month over day over weekday, for an instant the browser reads in its own zone. */
export function DateTile({ iso, small = false }: { iso: string | null; small?: boolean }) {
  const t = iso ? new Date(iso) : null;
  const valid = t !== null && Number.isFinite(t.getTime());
  if (!valid) {
    return (
      <span className={`date-tile undated ${small ? 'small' : ''}`} aria-hidden="true">
        <i className="bi bi-calendar-x" />
      </span>
    );
  }
  return (
    <time
      className={`date-tile ${small ? 'small' : ''}`}
      dateTime={t.toISOString()}
      title={t.toLocaleString('ca', { dateStyle: 'full', timeStyle: 'short' })}
    >
      <span className="month">
        {t.toLocaleDateString('ca', { month: 'short' }).replace('.', '')}
      </span>
      <span className="day num">{t.getDate()}</span>
      <span className="weekday">
        {t.toLocaleDateString('ca', { weekday: 'short' }).replace('.', '')}
      </span>
    </time>
  );
}

/** "21:30": the hour of an instant, for the line beside the tile. */
const hourOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' });

/**
 * The countdown as four figures over their units. Not a live region (it ticks
 * every second): a screen reader gets the sentence once, as plain text, and
 * the figures are decoration.
 */
export function Countdown({
  seconds,
  showSeconds = false,
}: {
  /** Seconds until the start; zero or less reads "starts now". */
  seconds: number;
  showSeconds?: boolean;
}) {
  const t = ca.sessionWhen;
  if (seconds <= 0) {
    return (
      <p className="when-now">
        <i className="bi bi-play-circle-fill" aria-hidden="true" /> {ca.live.startingNow}
      </p>
    );
  }
  const { days, hours, minutes, seconds: s } = splitSeconds(seconds);
  const units: { key: string; value: string; label: string }[] = [
    ...(days > 0 ? [{ key: 'd', value: String(days), label: t.days(days) }] : []),
    { key: 'h', value: two(hours), label: t.hours },
    { key: 'm', value: two(minutes), label: t.minutes },
    ...(showSeconds ? [{ key: 's', value: two(s), label: t.seconds }] : []),
  ];
  return (
    <div className="countdown-units" role="group" aria-label={t.label}>
      <span className="sr-only">{ca.live.until(seconds)}</span>
      {units.map((u) => (
        <span key={u.key} className="unit" aria-hidden="true">
          <strong className="num">{u.value}</strong>
          <span>{u.label}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * The whole block of a session that waits. With an hour: the date tile, the
 * day and hour in words and the countdown. Without: the same tile crossed out,
 * and what starts it instead: the director's hand.
 */
export function SessionWhen({
  scheduledAt,
  now,
  showSeconds = false,
}: {
  scheduledAt: string | null | undefined;
  now: number;
  showSeconds?: boolean;
}) {
  const t = ca.sessionWhen;
  const at = scheduledAt ? new Date(scheduledAt).getTime() : NaN;
  if (!Number.isFinite(at)) {
    return (
      <div className="session-when undated">
        <DateTile iso={null} />
        <div className="when-body">
          <strong>{t.undatedTitle}</strong>
          <span className="muted small">{t.undatedHint}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="session-when">
      <DateTile iso={scheduledAt ?? null} />
      <div className="when-body">
        <strong>
          {new Date(at).toLocaleDateString('ca', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </strong>
        <span className="muted small num">
          <i className="bi bi-clock" aria-hidden="true" /> {hourOf(scheduledAt as string)}
        </span>
      </div>
      <Countdown seconds={(at - now) / SECOND} showSeconds={showSeconds} />
    </div>
  );
}

/**
 * The compact form for a card of the sessions list: a small tile and one line
 * with the countdown (or the reason there is none).
 */
export function WhenLine({ scheduledAt, now }: { scheduledAt: string | null; now: number }) {
  const t = ca.sessionWhen;
  const at = scheduledAt ? new Date(scheduledAt).getTime() : NaN;
  if (!Number.isFinite(at)) {
    return (
      <div className="when-line undated">
        <DateTile iso={null} small />
        <span className="muted small">{t.undatedTitle}</span>
      </div>
    );
  }
  const seconds = (at - now) / SECOND;
  const { days, hours, minutes } = splitSeconds(seconds);
  return (
    <div className="when-line">
      <DateTile iso={scheduledAt} small />
      <div className="when-body">
        <span className="num">
          {new Date(at)
            .toLocaleDateString('ca', { day: 'numeric', month: 'short' })
            .replace('.', '')}{' '}
          · {hourOf(scheduledAt as string)}
        </span>
        <span
          className="chip countdown num"
          title={ca.live.startsIn(ca.live.until(Math.max(0, seconds)))}
        >
          <i className="bi bi-hourglass-split" aria-hidden="true" />
          {seconds <= 0
            ? ca.live.startingNow
            : `${days > 0 ? `${days} d ` : ''}${hours} h ${minutes} min`}
        </span>
      </div>
    </div>
  );
}
